import type { Command } from '../sim/commands';
import type { Simulation } from '../sim/sim';
import type { SimEvent } from '../sim/types';
import type { AudioEngine } from './engine';
import { CooldownLimiter, admitVoice, clinkRate, type Priority } from './mixerLogic';
import { GAME_TRACK, MusicPlayer, type MusicTrackId } from './music';
import { SFX, startWindBed } from './sfx';

const PRIORITY: Record<string, Priority> = {
  uiTick: 'low',
  clink: 'low',
  oreDrop: 'low',
  paperSlide: 'normal',
  placeThunk: 'normal',
  demolishCrumble: 'normal',
  truckOut: 'normal',
  busArrival: 'normal',
  coinSpill: 'normal',
  payrollThud: 'normal',
  deny: 'low',
  saleChaChing: 'high',
  trainWhistle: 'high',
  schoolBell: 'high',
  chapelBell: 'high',
  stamp: 'high',
  pressSting: 'high',
};

/**
 * The game's third event consumer, after the renderer and HUD: maps sim
 * events and player commands to sounds, and runs the scale-aware samplers.
 * Uses its own rng — never the sim's forks, which must stay deterministic.
 */
export class AudioLayer {
  readonly music: MusicPlayer;
  private readonly engine: AudioEngine;
  private readonly rng: () => number = Math.random;
  private readonly limiter = new CooldownLimiter();
  private windStop: (() => void) | null = null;
  // Dispatch-armed intents: 'buildingsChanged' can't tell a placement from a
  // demolition, and the UI dispatches placements unvalidated — so the sound
  // only plays when the intent is confirmed by the event within its TTL.
  private pendingPlace = 0;
  private pendingDemolish = 0;
  private haulersToDepot = new Set<number>();

  /** What plays once audio unlocks — the title theme when the menu is up,
   * the game theme otherwise. Switchable at runtime (title -> game). */
  initialTrack: MusicTrackId = GAME_TRACK;

  constructor(engine: AudioEngine) {
    this.engine = engine;
    this.music = new MusicPlayer(engine);
    engine.whenReady(() => {
      this.windStop ??= startWindBed(engine, this.rng);
      void this.music.play(this.initialTrack);
      // If that one-shot start failed (bad fetch, undecodable format), each
      // later resume — e.g. the gesture unlock — gets another attempt.
      engine.ctx?.addEventListener('statechange', () => {
        if (engine.ctx?.state === 'running' && this.music.playing === null) {
          void this.music.play(this.initialTrack);
        }
      });
    });
  }

  /** Player intent, straight off the command bus (UI-side only). */
  onCommand(command: Command): void {
    const now = performance.now();
    if (command.kind === 'placeBuilding') this.pendingPlace = now + 300;
    else if (command.kind === 'demolish') this.pendingDemolish = now + 300;
    else if (command.kind === 'dispatchTruck' || command.kind === 'setSpeed' || command.kind === 'setJobTargets') {
      this.play('uiTick');
    }
  }

  /** Once per frame from the game loop. */
  frame(sim: Simulation, events: readonly SimEvent[]): void {
    if (!this.engine.ctx) return;
    this.handleEvents(events);
    this.samplers(sim);
  }

  private handleEvents(events: readonly SimEvent[]): void {
    const now = performance.now();
    let residentsThisFrame = 0;
    let lettersThisFrame = 0;
    for (const event of events) {
      switch (event.kind) {
        case 'buildingsChanged':
          if (this.pendingPlace > now) {
            this.pendingPlace = 0;
            this.play('placeThunk');
          } else if (this.pendingDemolish > now) {
            this.pendingDemolish = 0;
            this.play('demolishCrumble');
          }
          break;
        case 'truckDeparted':
          this.play('truckOut');
          break;
        case 'sale':
          this.play('saleChaChing');
          break;
        case 'payroll':
          this.play(event.breakdown.missed ? 'payrollThud' : 'coinSpill');
          break;
        case 'hire':
          this.play('busArrival');
          break;
        case 'residentArrived':
          residentsThisFrame += 1;
          break;
        case 'letterDelivered':
        case 'familyRequest':
          lettersThisFrame += 1;
          break;
        case 'kidGrewUp':
          this.play('schoolBell');
          break;
        case 'familySettled':
          this.play('chapelBell');
          break;
        case 'incorporated':
          this.play('stamp');
          break;
        case 'endingReached':
          this.play('pressSting');
          break;
        default:
          break;
      }
    }
    if (residentsThisFrame > 0) this.play('trainWhistle');
    if (lettersThisFrame > 0) this.play('paperSlide');
  }

  /** Scale-aware ambience: the worksite clinks with the crew, never a storm. */
  private samplers(sim: Simulation): void {
    const state = sim.state;
    if (state.meta.speed <= 0 || state.meta.gameOver) return;
    const dtSeconds = 1 / 60; // rAF cadence; close enough for stochastic rates

    let digging = 0;
    const toDepotNow = new Set<number>();
    for (const peep of state.peeps) {
      if (peep.task.kind === 'digging') digging += 1;
      else if (peep.task.kind === 'toDepot') toDepotNow.add(peep.id);
    }
    if (this.rng() < clinkRate(digging) * dtSeconds) this.play('clink');

    // A hauler leaving the toDepot set just dropped their load.
    for (const id of this.haulersToDepot) {
      if (!toDepotNow.has(id)) {
        this.play('oreDrop');
        break;
      }
    }
    this.haulersToDepot = toDepotNow;
  }

  private play(name: string): void {
    const recipe = SFX[name];
    if (!recipe || !this.engine.ctx) return;
    if (!admitVoice(this.engine.activeVoices, PRIORITY[name] ?? 'normal')) return;
    if (!this.limiter.admit(name, performance.now())) return;
    recipe(this.engine, this.rng);
  }
}
